import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import { statusLabels, fileTypeLabels, AccessLabel, LABEL_HTML } from "../../contants";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import FormAction from "redux-react/actions/formAction";
import { useDispatch, useSelector } from "react-redux";
import {
	setKnowledgeBaseConfig,
	selectKnowledgeBaseConfig
} from "redux-react/reducers/knowledgeBaseReducer";
import MDTypography from "components/Basics/MDTypography";
import MDBox from "components/Basics/MDBox";
import DataCheckDialog from "./components/DataCheckDialog";
import MDButton from "components/Basics/MDButton";

const TYPE_DISPLAY_MAPPING = {
	htmlText: LABEL_HTML
};

const FILTER_CONTROL_SX = {
	height: "100%",
	"& .MuiOutlinedInput-root": {
		height: "100%",
		minHeight: 56,
		alignItems: "center"
	}
};

const KnowledgeBaseSelector = () => {
	const dispatch = useDispatch();
	const knowledgeBaseConfig = useSelector(selectKnowledgeBaseConfig);
	const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState([]);

	const handleKBChange = event => {
		const knowledgeBase = availableKnowledgeBases.find(kb => kb.code === event.target.value);
		dispatch(setKnowledgeBaseConfig(knowledgeBase));
	};

	useEffect(() => {
		dispatch(
			FormAction.getItemsFromCollection(
				"knowledgeBaseConfig",
				{
					query: {
						active: true
					},
					projection: {
						_id: 0,
						code: 1,
						name: 1,
						description: 1,
						testRuntimeCode: 1
					},
					sort: {
						name: 1
					}
				},
				res => {
					const knowledgeBases = res?.items ?? [];
					setAvailableKnowledgeBases(knowledgeBases);
					// Select the first knowledge base by default if available
					if (knowledgeBases.length > 0) {
						dispatch(setKnowledgeBaseConfig(knowledgeBases[0]));
					} else {
						dispatch(
							setKnowledgeBaseConfig({
								empty: true
							})
						);
					}
				}
			)
		);

		return () => {
			dispatch(
				setKnowledgeBaseConfig({
					empty: true
				})
			);
		};
	}, []);

	return (
		<FormControl
			sx={{
				...FILTER_CONTROL_SX,
				flex: 2
			}}
		>
			<InputLabel id="knowledge-base-select-label">Base de connaissances</InputLabel>
			<Select
				labelId="knowledge-base-select-label"
				id="knowledge-base-select"
				value={knowledgeBaseConfig?.code || ""}
				label="Base de connaissances"
				onChange={handleKBChange}
				disabled={knowledgeBaseConfig?.empty === true || availableKnowledgeBases.length === 0}
				renderValue={value => {
					const selectedKnowledgeBase = availableKnowledgeBases.find(kb => kb.code === value);
					const selectedName = selectedKnowledgeBase?.name || "";
					return (
						<MDTypography
							variant="h6"
							title={selectedName}
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap"
							}}
						>
							{selectedName}
						</MDTypography>
					);
				}}
				sx={{
					"& .MuiSelect-select": {
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}
				}}
			>
				{availableKnowledgeBases.map(kb => (
					<MenuItem key={kb.code} value={kb.code} sx={{ display: "block", maxWidth: "100%" }}>
						<MDTypography
							variant="h6"
							title={kb.name}
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap"
							}}
						>
							{kb.name}
						</MDTypography>
						<MDTypography
							variant="caption"
							fontSize="small"
							color="text.secondary"
							display="block"
							title={kb.description}
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap"
							}}
						>
							{kb.description}
						</MDTypography>
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

const AssetFilters = ({ filters, onFilterChange }) => {
	const { t } = useTranslation();

	const knowledgeBaseConfig = useSelector(selectKnowledgeBaseConfig);
	const permissions = useSelector(state => state.permissions?.permissions);
	const profile = useSelector(state => state.profile);
	const hasAdvancedSettings = permissions.includes("exec:faiqSettingsAdvancedSettings");

	const [dataCheckDialogOpen, setDataCheckDialogOpen] = useState(false);

	const handleChange = filterType => (event, newValue) => {
		onFilterChange(filterType, newValue);
	};

	const getTypeDisplayLabel = option => {
		return TYPE_DISPLAY_MAPPING[option] || t(`ASSETS.TYPE.${option}`, { defaultValue: option });
	};

	return (
		<Box sx={{ display: "flex", gap: 2, alignItems: "stretch", width: "100%" }}>
			{/* Knowledge base selector */}
			<KnowledgeBaseSelector key="kb-selector" />
			{/* Status Filter */}
			<Autocomplete
				multiple
				id="status-filter"
				options={statusLabels.map(option => option.value)}
				getOptionLabel={option => t(`ASSETS.STATUS.${option}`, { defaultValue: option })}
				value={filters.status || []}
				onChange={handleChange("status")}
				filterSelectedOptions
				sx={{
					...FILTER_CONTROL_SX,
					flex: 1,
					"& .MuiAutocomplete-inputRoot": {
						flexWrap: "nowrap"
					}
				}}
				limitTags={1}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => {
						const { key, ...tagProps } = getTagProps({ index });
						return (
							<Chip
								key={key}
								label={t(`ASSETS.STATUS.${option}`, { defaultValue: option })}
								{...tagProps}
							/>
						);
					})
				}
				renderInput={params => (
					<TextField
						{...params}
						label={t("SOURCES.FILTERS.status")}
						placeholder={t("SOURCES.FILTERS.selectStatus")}
						sx={FILTER_CONTROL_SX}
					/>
				)}
			/>
			{/* Access Filter */}
			<Autocomplete
				multiple
				id="access-filter"
				options={AccessLabel.map(option => option.value)}
				getOptionLabel={option => t(`ASSETS.ACCESS.${option}`, { defaultValue: option })}
				value={filters.access || []}
				onChange={handleChange("access")}
				filterSelectedOptions
				sx={{
					...FILTER_CONTROL_SX,
					flex: 1,
					"& .MuiAutocomplete-inputRoot": {
						flexWrap: "nowrap"
					}
				}}
				limitTags={1}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => {
						const { key, ...tagProps } = getTagProps({ index });
						return (
							<Chip
								key={key}
								label={t(`ASSETS.ACCESS.${option}`, { defaultValue: option })}
								{...tagProps}
							/>
						);
					})
				}
				renderInput={params => (
					<TextField
						{...params}
						label={t("SOURCES.FILTERS.access")}
						placeholder={t("SOURCES.FILTERS.selectAccess")}
						sx={FILTER_CONTROL_SX}
					/>
				)}
			/>
			{/* File Type Filter */}
			<Autocomplete
				multiple
				id="type-filter"
				options={fileTypeLabels.map(option => option.value)}
				getOptionLabel={getTypeDisplayLabel}
				value={filters.type || []}
				onChange={handleChange("type")}
				filterSelectedOptions
				sx={{
					...FILTER_CONTROL_SX,
					flex: 1,
					"& .MuiAutocomplete-inputRoot": {
						flexWrap: "nowrap"
					}
				}}
				limitTags={1}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => {
						const { key, ...tagProps } = getTagProps({ index });
						return <Chip key={key} label={getTypeDisplayLabel(option)} {...tagProps} />;
					})
				}
				renderInput={params => (
					<TextField
						{...params}
						label={t("SOURCES.FILTERS.type")}
						placeholder={t("SOURCES.FILTERS.selectType")}
						sx={FILTER_CONTROL_SX}
					/>
				)}
			/>
			{/* Bijection check */}
			{hasAdvancedSettings && (
				<>
					<MDButton
						sx={{ ml: 2 }}
						variant="outlined"
						color="info"
						onClick={() => setDataCheckDialogOpen(true)}
					>
						{t("CONFIG.dataCheck.button")}
					</MDButton>

					{/* Data integrity check dialog */}
					<DataCheckDialog
						open={dataCheckDialogOpen}
						onClose={() => setDataCheckDialogOpen(false)}
						assistantID={profile?.assistantID}
						vectorModel={knowledgeBaseConfig?.models?.vectorModel}
						embeddingsModel={knowledgeBaseConfig?.models?.embeddingsModel}
					/>
				</>
			)}
		</Box>
	);
};

export default AssetFilters;
